import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Building2, Clock, Settings, TrendingDown, Users } from "lucide-react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { WriteModeBanner } from "../components/write-mode-banner";
import { ChangeList } from "../components/change-list";

/**
 * The External API dashboard — one of the three areas on the B2B landing page.
 *
 * It leads with the two things the legacy tool never showed: which operations are
 * currently allowed to write, and which clients are silently broken (a SingleFeature
 * client with no grants gets 401 on every call, and nothing surfaced that).
 */
export default function B2bExternalApi() {
  const summary = useQuery({ queryKey: ["b2b", "summary"], queryFn: api.b2b.summary });

  const counters = summary.data?.clients;

  return (
    <div className="space-y-6">
      <div>
        <Link className="text-sm text-muted-foreground underline" to="/dashboard/b2b">
          ← B2B
        </Link>
        <h1 className="text-2xl font-bold">B2B — External API</h1>
        <p className="text-sm text-muted-foreground">API clients, their endpoint access and usage</p>
      </div>

      {summary.data && <WriteModeBanner modes={summary.data.writeModes} />}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="API clients" value={counters?.total} icon={Building2} />
        <Stat label="Active" value={counters?.active} icon={Users} />
        <Stat
          label="No grants"
          value={counters?.withoutGrants}
          icon={AlertTriangle}
          tone={counters?.withoutGrants ? "warn" : undefined}
          hint="Single-feature clients that can call nothing"
        />
        <Stat
          label="Pending propagation"
          value={summary.data?.propagationPending}
          icon={Clock}
          tone={summary.data?.propagationPending ? "warn" : undefined}
          hint="Cache evictions a runtime has not confirmed"
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <SectionCard
          to="/dashboard/b2b/clients"
          icon={Building2}
          title="Clients"
          description="Keys, tokens, endpoint access, feature flags and scopes"
        />
        <SectionCard
          to="/dashboard/b2b/usage"
          icon={Clock}
          title="Usage"
          description="Requests by client and endpoint, from Application Insights"
        />
        <SectionCard
          to="/dashboard/b2b/api-usage"
          icon={TrendingDown}
          title="Usage monitor"
          description="Calls per key each week and month; flags a fall of more than 20%"
        />
        <SectionCard
          to="/dashboard/b2b/settings"
          icon={Settings}
          title="Settings"
          description="Write modes, notification recipients, propagation health"
        />
      </div>

      <div className="rounded-xl border bg-card">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <div className="flex items-baseline gap-3">
            <h2 className="font-semibold">Recent changes</h2>
            <Link className="text-sm text-muted-foreground underline" to="/dashboard/b2b/changes">
              All changes
            </Link>
          </div>
          {summary.data?.notifyFailures ? (
            <Badge variant="destructive">
              {summary.data.notifyFailures} notification{summary.data.notifyFailures === 1 ? "" : "s"} failed
            </Badge>
          ) : null}
        </div>
        <div className="p-2">
          <ChangeList
            changes={summary.data?.recentChanges ?? []}
            loading={summary.isLoading}
            // The landing page is a glance, not a workbench: Revert belongs on the
            // client's History tab or the change log, where the full record is in view.
            showRevert={false}
            linkToRecord
            emptyMessage="No changes recorded yet."
          />
        </div>
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  icon: Icon,
  tone,
  hint,
}: {
  label: string;
  value: number | undefined;
  icon: typeof Building2;
  tone?: "warn";
  hint?: string;
}) {
  return (
    <div className="rounded-xl border bg-card p-5">
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">{label}</span>
        <Icon
          className={`h-4 w-4 ${tone === "warn" ? "text-amber-600" : "text-muted-foreground"}`}
        />
      </div>
      <div
        className={`mt-2 text-3xl font-semibold tabular-nums ${
          tone === "warn" ? "text-amber-600" : ""
        }`}
      >
        {value ?? "—"}
      </div>
      {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

function SectionCard({
  to,
  icon: Icon,
  title,
  description,
}: {
  to: string;
  icon: typeof Building2;
  title: string;
  description: string;
}) {
  return (
    <Link
      to={to}
      className="group flex flex-col rounded-xl border bg-card p-6 transition-all hover:border-primary/50 hover:shadow-lg hover:shadow-primary/5"
    >
      <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
        <Icon className="h-6 w-6" />
      </div>
      <h3 className="mb-1 text-lg font-semibold">{title}</h3>
      <p className="text-sm text-muted-foreground">{description}</p>
    </Link>
  );
}
